import { useEffect, useState } from "react"
import Accordion from "@mui/material/Accordion"
import AccordionSummary from "@mui/material/AccordionSummary"
import AccordionDetails from "@mui/material/AccordionDetails"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import ScienceIcon from "@mui/icons-material/Science"
import { AppStoreRating } from "../rating/AppStoreRating"
import { OurRating } from "../rating/OurRating"
import { providersList } from "../../utils/helpers"

interface CansListProps {
  type?: string
  state?: string
  cans: any[]
  provider?: string
}

/** Generic study themes when themeTitle is not set (fallback for legacy data) */
const DEFAULT_THEME_TITLE = "Platform Experience Study"
/** Generic scope when scopeSummary is not set */
const DEFAULT_SCOPE_SUMMARY =
  "Explore key platform features, account controls, and responsible play settings."

/** Brand-agnostic instructions - no operator names */
const BRAND_AGNOSTIC_INSTRUCTIONS = [
  'Click the "Start" button.',
  "You will be redirected to the platform. Create an account using the platform's signup flow.",
  "Locate account controls and complete the required platform interactions.",
  "Find responsible play settings within the platform.",
  "Once you have completed the required steps, provide a screenshot of your Welcome Email and BetSlip to your designated PlayCan Customer Service Ambassador.",
  "Ensure you have completed the PlayCan feedback survey located at the top of this page.",
  "Upon completion of the steps above you will be compensated in full for participation in this study.",
]

export function CansList({ state, type, cans, provider }: CansListProps) {
  const [data, setData] = useState(cans)

  useEffect(() => {
    let finalList = cans
    if (state) {
      finalList = finalList.filter((item) => item.state?.slug === state)
    }
    if (type) {
      finalList = finalList.filter((item) => item.type === type.toUpperCase())
    }

    finalList = finalList.filter((item) => item.status)
    finalList.sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0))
    setData(finalList)
  }, [type, cans, state])

  const convertUrl = (url: string): string => btoa(url)

  const isUnmasked = (item: any): boolean =>
    item.file?.brandMaskLevel === "visible"

  const getOperatorName = (item: any): string =>
    item.file?.betlabName || item.text || ""

  const getDescription = (item: any): string =>
    item.file?.betlabDescription || item.description || ""

  const getThemeTitle = (item: any): string =>
    item.file?.themeTitle || DEFAULT_THEME_TITLE

  const getScopeSummary = (item: any): string =>
    item.file?.scopeSummary || DEFAULT_SCOPE_SUMMARY

  const getInstructions = (_item: any): string[] => BRAND_AGNOSTIC_INSTRUCTIONS

  const getUnmaskedInstructions = (item: any): string[] => {
    const operatorName = getOperatorName(item)
    return [
      'Click "Start" Button',
      `You will be redirected to ${operatorName}. Follow the signup instructions on the ${operatorName} website and create an account on their website.`,
      "Deposit and Play on their website.",
      "Once your bets have been placed, provide a screenshot of your Welcome Email and BetSlip to your designated PlayCan Customer Service Ambassador.",
      "Ensure you have completed the PlayCan feedback survey located at the top of this page.",
      "Upon completion of the steps above you will be compensated in full for participation in this study.",
    ]
  }

  return (
    <div>
      {data.map((item) => {
        const providerId = item.providerId ? item.providerId : 0
        const fileId = item.file ? item.file.id : 0
        const image_url = item.file?.filename
          ? `${import.meta.env.VITE_STATIC_SERVER}${item.file.filename}`
          : ""
        const unmasked = isUnmasked(item)
        const instructions = unmasked
          ? getUnmaskedInstructions(item)
          : getInstructions(item)

        let casinoLink = item.url;

        return (
          <div className="card-item" key={item.id}>
            <div className="card-item-img">
              <div
                className={`image object-fit ${!unmasked ? "card-icon-placeholder" : ""}`}
              >
                {unmasked && image_url ? (
                  <img
                    className="img-fluid img-1"
                    src={image_url}
                    alt=""
                    loading="lazy"
                  />
                ) : (
                  <span className="card-icon-inner">
                    <ScienceIcon className="card-study-icon" aria-hidden />
                  </span>
                )}
              </div>
              {providersList.includes(provider ?? "") ? (
                <OurRating value={item.rating} />
              ) : (
                <AppStoreRating value={item.rating} />
              )}
              <div className="heading-mobile">
                <h4>
                { unmasked ? getOperatorName(item) : getThemeTitle(item) }
                </h4>
                <h5 className="card-scope">
                  {unmasked ? getDescription(item) : getScopeSummary(item)}
                </h5>
              </div>
            </div>
            <div className="content-info">
              <div className="card-item-payment">
                <h4>
                { unmasked ? getOperatorName(item) : getThemeTitle(item) }
                </h4>
                <h5 className="card-scope">
                  {unmasked ? getDescription(item) : getScopeSummary(item)}
                </h5>
               
              </div>
              <a
                href={casinoLink}
                target="_blank"
                rel="noopener noreferrer"
                className="card-item-claim"
              >
                Play Now
              </a>
              <div className="can_instruction">
                  <Accordion
                    sx={{
                      backgroundColor: "transparent",
                      boxShadow: "none",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon sx={{ color: "#fff" }} />}
                      aria-controls={`panel-${item.id}-content`}
                      id={`panel-${item.id}-header`}
                      sx={{ color: "#fff", padding: 0, minHeight: "auto" }}
                    >
                      <span className="text-size-regular">
                        View instructions
                      </span>
                    </AccordionSummary>
                    <AccordionDetails sx={{ padding: "0 0 16px 0" }}>
                      <ol style={{ color: "#ccc", margin: 0, paddingLeft: 20 }}>
                        {instructions.map((step, idx) => (
                          <li key={idx} style={{ marginBottom: 8 }}>
                            {step}
                          </li>
                        ))}
                      </ol>
                    </AccordionDetails>
                  </Accordion>
                </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
